import { useEffect, useState } from "react";
import { Building, Users, Bell, Shield, Settings } from "lucide-react";
import { Card } from "@/ui/components/primitives/card";
import { Button } from "@/ui/components/primitives/button";
import { Input } from "@/ui/components/primitives/input";
import { Label } from "@/ui/components/primitives/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/ui/components/primitives/tabs";
import { Switch } from "@/ui/components/primitives/switch";
import { Badge } from "@/ui/components/primitives/badge";
import { Separator } from "@/ui/components/primitives/separator";
import { Alert, AlertDescription, AlertTitle } from "@/ui/components/primitives/alert";
import { getMe, updateMe } from "@/services/api/services/profile";
import { showSuccessToast, showErrorToast } from "@/services/toast-helper";
import { getMFAStatus } from "@/services/api/services/mfa";
import { MFASetupModal } from "@/modules/auth/components/mfa-setup-modal";
import { MFADisableModal } from "@/modules/auth/components/mfa-disable-modal";
import { ChangePasswordModal } from "@/modules/settings/components/change-password-modal";
import type { MFAStatus } from "@/modules/shared/types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/components/primitives/select";

export function SettingsPage() {
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [merchantName, setMerchantName] = useState("Acme Corporation");
  const [merchantStatus, setMerchantStatus] = useState("ACTIVE");
  const [ownerName, setOwnerName] = useState("-");
  const [contactEmail, setContactEmail] = useState("admin@acmecorp.com");
  const [contactPhone, setContactPhone] = useState("+91 98765 43210");
  const [saving, setSaving] = useState(false);

  // MFA State
  const [mfaStatus, setMfaStatus] = useState<MFAStatus>({ enabled: false });
  const [mfaSetupOpen, setMfaSetupOpen] = useState(false);
  const [mfaDisableOpen, setMfaDisableOpen] = useState(false);

  // Change Password State
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  // Load MFA status
  useEffect(() => {
    (async () => {
      try {
        const status = await getMFAStatus();
        setMfaStatus(status);
      } catch (error) {
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoadingProfile(true); setProfileError("");
      try {
        const user = await getMe();
        setOwnerName(user.fullName || "-");
        setContactEmail(user.email || "-");
        setContactPhone(user.phone || "-");
        const mu = (user.merchantUsers || [])[0];
        if (mu?.merchant) {
          setMerchantName(mu.merchant.name || merchantName);
          setMerchantStatus(mu.merchant.status || merchantStatus);
        }
      } catch (e) {
        setProfileError("Failed to load profile");
      } finally { setLoadingProfile(false); }
    })();
  }, []);

  // Handle MFA enable/disable
  const handleMFAEnabled = () => {
    setMfaStatus({ enabled: true, method: "TOTP" });
    setMfaSetupOpen(false);
  };

  const handleMFADisabled = () => {
    setMfaStatus({ enabled: false });
    setMfaDisableOpen(false);
  };
  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden min-w-0">
      {/* Header */}
      <div>
        <h1>Settings</h1>
        <p className="text-muted-foreground mt-1">
          Manage your business profile and preferences
        </p>
      </div>

      <Tabs defaultValue="business" className="space-y-6">
        <TabsList className="flex-wrap">
          <TabsTrigger value="business" className="gap-2">
            <Building className="h-4 w-4" strokeWidth={1.5} />
            Business
          </TabsTrigger>
              {/* <TabsTrigger value="team" className="gap-2">
            <Users className="h-4 w-4" strokeWidth={1.5} />
            Team
          </TabsTrigger> */}
          {/* <TabsTrigger value="notifications" className="gap-2">
            <Bell className="h-4 w-4" strokeWidth={1.5} />
            Notifications
          </TabsTrigger> */}
          <TabsTrigger value="security" className="gap-2">
            <Shield className="h-4 w-4" strokeWidth={1.5} />
            Security
          </TabsTrigger>
        </TabsList>

        {/* Business Tab */}
        <TabsContent value="business">
          <Card className="p-6 min-w-0">
            <div className="space-y-6">
              <div>
                <h3 className="mb-1">Business Information</h3>
                <p className="text-sm text-muted-foreground">
                  Update your business details and KYC information
                </p>
              </div>

              <div className="flex items-center gap-4 p-4 bg-muted rounded-lg">
                <div className="h-16 w-16 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Building className="h-8 w-8 text-primary" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4>{merchantName}</h4>
                    <Badge className={merchantStatus === "ACTIVE" ? "bg-success/10 text-success border-success/20" : "bg-warning/10 text-warning border-warning/20"}>
                      {merchantStatus}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Owner: {ownerName}
                  </p>
                </div>
              </div>

              {profileError && (
                <Alert>
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{profileError}</AlertDescription>
                </Alert>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 min-w-0">
                <div className="space-y-2">
                  <Label htmlFor="business-name">Legal Business Name</Label>
                  <Input id="business-name" value={merchantName} onChange={(e) => setMerchantName(e.target.value)} className="w-full" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="business-type">Business Type</Label>
                  <Select>
                    <SelectTrigger id="business-type" className="w-full">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="private">Private Limited</SelectItem>
                      <SelectItem value="public">Public Limited</SelectItem>
                      <SelectItem value="partnership">Partnership</SelectItem>
                      <SelectItem value="proprietorship">Proprietorship</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 min-w-0">
                <div className="space-y-2">
                  <Label htmlFor="gstin">GSTIN</Label>
                  <Input id="gstin" className="w-full" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pan">PAN</Label>
                  <Input id="pan" className="w-full" />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Registered Address</Label>
                <Input id="address" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 min-w-0">
                <div className="space-y-2">
                  <Label htmlFor="contact-email">Contact Email</Label>
                  <Input id="contact-email" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className="w-full" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-phone">Contact Phone</Label>
                  <Input id="contact-phone" type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} className="w-full" />
                </div>
              </div>

              <Separator />

              <div className="flex gap-3">
                <Button
                  onClick={async () => {
                    setSaving(true);
                    try {
                      const updated = await updateMe({ fullName: ownerName, phone: contactPhone, email: contactEmail });
                      if (updated) {
                        showSuccessToast("Profile updated", "Your changes have been saved");
                      } else {
                        showErrorToast("Update failed", "Please try again");
                      }
                    } catch {
                      showErrorToast("Update failed", "Please try again");
                    } finally { setSaving(false); }
                  }}
                  loading={saving}
                  loadingText="Saving..."
                >
                  Save Changes
                </Button>
                <Button variant="outline"
                  onClick={async () => {
                    setLoadingProfile(true);
                    try {
                      const user = await getMe();
                      setOwnerName(user.fullName || "-");
                      setContactEmail(user.email || "-");
                      setContactPhone(user.phone || "-");
                      const mu = (user.merchantUsers || [])[0];
                      if (mu?.merchant) {
                        setMerchantName(mu.merchant.name || merchantName);
                        setMerchantStatus(mu.merchant.status || merchantStatus);
                      }
                    } catch {}
                    setLoadingProfile(false);
                  }}
                  loading={loadingProfile}
                  loadingText="Refreshing..."
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>

  
        {/* Team Tab */}
        <TabsContent value="team">
          <Card className="p-6 min-w-0">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="mb-1">Team Members</h3>
                  <p className="text-sm text-muted-foreground">
                    Manage team access and permissions
                  </p>
                </div>
                <Button>Invite Member</Button>
              </div>

              <div className="space-y-3">
                {[
                  {
                    name: "John Doe",
                    email: "john@acmecorp.com",
                    role: "Admin",
                    status: "Active",
                  },
                  {
                    name: "Jane Smith",
                    email: "jane@acmecorp.com",
                    role: "Manager",
                    status: "Active",
                  },
                  {
                    name: "Robert Wilson",
                    email: "robert@acmecorp.com",
                    role: "Developer",
                    status: "Active",
                  },
                ].map((member, index) => (
                  <div
                    key={index}
                    className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 p-4 border rounded-lg min-w-0"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="font-medium text-primary">
                          {member.name.split(" ").map((n) => n[0]).join("")}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium">{member.name}</p>
                        <p className="text-sm text-muted-foreground truncate max-w-[60%]">{member.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 w-full md:w-auto flex-wrap justify-start md:justify-end">
                      <Badge variant="secondary">{member.role}</Badge>
                      <Badge className="bg-success/10 text-success border-success/20">
                        {member.status}
                      </Badge>
                      <Button variant="ghost" size="sm">
                        Edit
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card className="p-6 min-w-0">
            <div className="space-y-6">
              <div>
                <h3 className="mb-1">Notification Preferences</h3>
                <p className="text-sm text-muted-foreground">
                  Choose how you want to receive notifications
                </p>
              </div>

              <div className="space-y-6">
                <div>
                  <h4 className="mb-4">Payment Notifications</h4>
                  <div className="space-y-4">
                    {[
                      {
                        title: "Successful Payments",
                        description: "Get notified when a payment is successful",
                      },
                      {
                        title: "Failed Payments",
                        description: "Get alerts for failed payment attempts",
                      },
                      {
                        title: "Pending Payments",
                        description: "Notifications for pending payments",
                      },
                    ].map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-4 border rounded-lg"
                      >
                        <div>
                          <p className="font-medium">{item.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {item.description}
                          </p>
                        </div>
                        <Switch defaultChecked />
                      </div>
                    ))}
                  </div>
                </div>

                <Separator />

                <div>
                  <h4 className="mb-4">Settlement Notifications</h4>
                  <div className="space-y-4">
                    {[
                      {
                        title: "Settlement Completed",
                        description: "When funds are settled to your bank",
                      },
                      {
                        title: "Settlement Scheduled",
                        description: "Upcoming settlement reminders",
                      },
                    ].map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-4 border rounded-lg"
                      >
                        <div>
                          <p className="font-medium">{item.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {item.description}
                          </p>
                        </div>
                        <Switch defaultChecked />
                      </div>
                    ))}
                  </div>
                </div>

                <Separator />

                <div>
                  <h4 className="mb-4">Security Notifications</h4>
                  <div className="space-y-4">
                    {[
                      {
                        title: "Login Alerts",
                        description: "Get notified of new login attempts",
                      },
                      {
                        title: "API Key Changes",
                        description: "When API keys are generated or modified",
                      },
                    ].map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-4 border rounded-lg"
                      >
                        <div>
                          <p className="font-medium">{item.title}</p>
                          <p className="text-sm text-muted-foreground">
                            {item.description}
                          </p>
                        </div>
                        <Switch defaultChecked />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <Card className="p-6 min-w-0">
            <div className="space-y-6">
              <div>
                <h3 className="mb-1">Security Settings</h3>
                <p className="text-sm text-muted-foreground">
                  Manage your account security preferences
                </p>
              </div>

              <div className="space-y-4">
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Two-Factor Authentication</p>
                      <p className="text-sm text-muted-foreground">
                        {mfaStatus.enabled
                          ? "Your account is protected with 2FA"
                          : "Add an extra layer of security to your account"
                        }
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        className={mfaStatus.enabled
                          ? "bg-success/10 text-success border-success/20"
                          : "bg-muted/10 text-muted-foreground border-muted/20"
                        }
                      >
                        {mfaStatus.enabled ? "Enabled" : "Disabled"}
                      </Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (mfaStatus.enabled) {
                            setMfaDisableOpen(true);
                          } else {
                            setMfaSetupOpen(true);
                          }
                        }}
                      >
                        {mfaStatus.enabled ? "Manage" : "Setup"}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-medium">Change Password</p>
                      <p className="text-sm text-muted-foreground">
                        Last changed 30 days ago
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setChangePasswordOpen(true)}>
                      Update
                    </Button>
                  </div>
                </div>

                {/* <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Active Sessions</p>
                      <p className="text-sm text-muted-foreground">
                        Manage devices where you're logged in
                      </p>
                    </div>
                    <Button variant="outline" size="sm">
                      View All
                    </Button>
                  </div>
                </div> */}

                {/* <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">IP Whitelist</p>
                      <p className="text-sm text-muted-foreground">
                        Restrict access to specific IP addresses
                      </p>
                    </div>
                    <Button variant="outline" size="sm">
                      Configure
                    </Button>
                  </div>
                </div> */}
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* MFA Modals */}
      <MFASetupModal
        open={mfaSetupOpen}
        onOpenChange={setMfaSetupOpen}
        onMFAEnabled={handleMFAEnabled}
      />
      <MFADisableModal
        open={mfaDisableOpen}
        onOpenChange={setMfaDisableOpen}
        onMFADisabled={handleMFADisabled}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        open={changePasswordOpen}
        onOpenChange={setChangePasswordOpen}
      />
    </div>
  );
}
